import dayjs from "dayjs";

/**
 * Grouping helpers for a chat thread. Pure, and channel-agnostic: WhatsApp
 * messages carry `created_at`, the unified envelope carries `sent_at`, so the
 * timestamp key is a parameter rather than an assumption.
 */

export const getDateLabel = (value) => {
  const date = dayjs(value);
  const today = dayjs();
  if (date.isSame(today, "day")) return "Today";
  if (date.isSame(today.subtract(1, "day"), "day")) return "Yesterday";
  if (date.isSame(today, "year")) return date.format("DD MMMM");
  return date.format("DD MMMM YYYY");
};

/** Interleave `{_type: "date-separator"}` rows wherever the day changes. */
export const withDateSeparators = (messages, timestampKey = "created_at") => {
  const out = [];
  let lastLabel = null;
  (messages || []).forEach((message) => {
    const label = getDateLabel(message[timestampKey]);
    if (label !== lastLabel) {
      out.push({ _type: "date-separator", label, key: `date-${label}` });
      lastLabel = label;
    }
    out.push(message);
  });
  return out;
};

/**
 * Strip the reply/forward prefixes so "Quote", "Re: Quote" and "RE: FW: Quote"
 * count as one subject.
 *
 * Email conversations here are one continuous chat per person, so the thread
 * shows a divider when the topic genuinely changes — which only reads right if
 * a reply is not mistaken for a new topic.
 */
export const normalizeSubject = (subject) => {
  let value = String(subject || "").trim();
  let previous;
  do {
    previous = value;
    value = value.replace(/^(re|fw|fwd)\s*(\[\d+\])?\s*:\s*/i, "").trim();
  } while (value !== previous);
  return value.replace(/\s+/g, " ").toLowerCase();
};

/** Interleave `{_type: "subject-divider"}` rows wherever the topic changes. */
export const withSubjectDividers = (messages) => {
  const out = [];
  let lastSubject = null;
  (messages || []).forEach((message, index) => {
    if (message._type) {
      out.push(message);
      return;
    }
    const normalized = normalizeSubject(message.subject);
    if (normalized && normalized !== lastSubject) {
      // The very first subject is the thread's own title, already shown in the
      // header, so it does not need a divider of its own.
      if (lastSubject !== null || index > 0) {
        out.push({
          _type: "subject-divider",
          label: message.subject,
          key: `subject-${message.id}`,
        });
      }
      lastSubject = normalized;
    }
    out.push(message);
  });
  return out;
};
